/**
 * DetailPanel — dispatcher that picks the right layer-specific detail
 * component based on `selectedType` and renders it inside a Suspense +
 * DetailErrorBoundary boundary.
 *
 * Every detail component (HeritageDetail, FolkloreDetail, RunestoneDetail …)
 * is lazy-loaded so each layer's panel is its own code-split chunk —
 * opening a runestone never pulls in the folklore panel's bundle.
 *
 * Consumed by App.tsx whenever a pin is selected.
 */
import React, { Suspense } from "react";
import { lazyWithStrings } from "../i18n/lazyComponent.ts";
import type { Lang, LayerType, EnrichmentData, UserData, UserSiteVisit, BadgeDef, UserCollection, CollectionItem, NearbyPinItem, FolkloreRecord, FolkloreContext, RuneStone, HistoricalEvent, Monument, ProtectedBuilding, Shipwreck, Museum, RockArt, UnescoSite, IndustrialSite, BarrowSite, MegalithSite, TownscapeSite, PlaqueSite, StolpersteinSite, ThingSite, HistoricRoute, ByleksikonSite, CuratedWalk, WalkStop } from "../types/index.ts";
import type { WikidataRelation } from "../api/graph.ts";
import { DetailErrorBoundary } from "./DetailErrorBoundary.tsx";
import { EnrichPendingContext } from "./enrichPending.ts";

// Lazy-load detail components (code-split per detail type)
/**
 * Each layer's panel module, by layer type, for preloadDetail below. The same
 * paths as the lazy components, so warming one makes React.lazy resolve
 * from the module cache.
 */
const PANEL_MODULES: Record<string, () => Promise<unknown>> = {
  heritage: () => import("./details/HeritageDetail.tsx"),
  lighthouse: () => import("./details/LighthouseDetail.tsx"),
  folklore: () => import("./details/FolkloreDetail.tsx"),
  runestone: () => import("./details/RunestoneDetail.tsx"),
  battlefield: () => import("./details/HistoricalEventDetail.tsx"),
  monument: () => import("./details/MonumentDetail.tsx"),
  building: () => import("./details/BuildingDetail.tsx"),
  shipwreck: () => import("./details/ShipwreckDetail.tsx"),
  museum: () => import("./details/MuseumDetail.tsx"),
  rockart: () => import("./details/RockArtDetail.tsx"),
  unesco: () => import("./details/UnescoDetail.tsx"),
  industrial: () => import("./details/IndustrialDetail.tsx"),
  barrow: () => import("./details/BarrowDetail.tsx"),
  megalith: () => import("./details/MegalithDetail.tsx"),
  townscape: () => import("./details/TownscapeDetail.tsx"),
  plaque: () => import("./details/PlaqueDetail.tsx"),
  stolperstein: () => import("./details/StolpersteinDetail.tsx"),
  thingsite: () => import("./details/ThingSiteDetail.tsx"),
  route: () => import("./details/RouteDetail.tsx"),
  byleksikon: () => import("./details/ByleksikonDetail.tsx"),
  walk: () => import("./details/CuratedWalkDetail.tsx"),
};

/**
 * Starts fetching the panel for `layer` and the lazy half of the strings, so
 * a reader who landed on a record does not wait for them after its data
 * arrives. On a phone on 4G the panel stood empty for 12 s, because both were
 * asked for only then, behind 2 MB of other layers (measured 1 Oct 2026).
 */
export function preloadDetail(layer: string): void {
  PANEL_MODULES[layer]?.().catch(() => {});
  import("../i18n/lazy.ts").catch(() => {});
}

const HeritageDetail = lazyWithStrings(() => import("./details/HeritageDetail.tsx").then(m => ({ default: m.HeritageDetail })));
const LighthouseDetail = lazyWithStrings(() => import("./details/LighthouseDetail.tsx").then(m => ({ default: m.LighthouseDetail })));
const FolkloreDetail = lazyWithStrings(() => import("./details/FolkloreDetail.tsx").then(m => ({ default: m.FolkloreDetail })));
const RunestoneDetail = lazyWithStrings(() => import("./details/RunestoneDetail.tsx").then(m => ({ default: m.RunestoneDetail })));
const HistoricalEventDetail = lazyWithStrings(() => import("./details/HistoricalEventDetail.tsx").then(m => ({ default: m.HistoricalEventDetail })));
const MonumentDetail = lazyWithStrings(() => import("./details/MonumentDetail.tsx").then(m => ({ default: m.MonumentDetail })));
const BuildingDetail = lazyWithStrings(() => import("./details/BuildingDetail.tsx").then(m => ({ default: m.BuildingDetail })));
const ShipwreckDetail = lazyWithStrings(() => import("./details/ShipwreckDetail.tsx").then(m => ({ default: m.ShipwreckDetail })));
const MuseumDetail = lazyWithStrings(() => import("./details/MuseumDetail.tsx").then(m => ({ default: m.MuseumDetail })));
const RockArtDetail = lazyWithStrings(() => import("./details/RockArtDetail.tsx").then(m => ({ default: m.RockArtDetail })));
const UnescoDetail = lazyWithStrings(() => import("./details/UnescoDetail.tsx").then(m => ({ default: m.UnescoDetail })));
const IndustrialDetail = lazyWithStrings(() => import("./details/IndustrialDetail.tsx").then(m => ({ default: m.IndustrialDetail })));
const BarrowDetail = lazyWithStrings(() => import("./details/BarrowDetail.tsx").then(m => ({ default: m.BarrowDetail })));
const MegalithDetail = lazyWithStrings(() => import("./details/MegalithDetail.tsx").then(m => ({ default: m.MegalithDetail })));
const TownscapeDetail = lazyWithStrings(() => import("./details/TownscapeDetail.tsx").then(m => ({ default: m.TownscapeDetail })));
const PlaqueDetail = lazyWithStrings(() => import("./details/PlaqueDetail.tsx").then(m => ({ default: m.PlaqueDetail })));
const StolpersteinDetail = lazyWithStrings(() => import("./details/StolpersteinDetail.tsx").then(m => ({ default: m.StolpersteinDetail })));
const ThingSiteDetail = lazyWithStrings(() => import("./details/ThingSiteDetail.tsx").then(m => ({ default: m.ThingSiteDetail })));
const RouteDetail = lazyWithStrings(() => import("./details/RouteDetail.tsx").then(m => ({ default: m.RouteDetail })));
const ByleksikonDetail = lazyWithStrings(() => import("./details/ByleksikonDetail.tsx").then(m => ({ default: m.ByleksikonDetail })));
const CuratedWalkDetail = lazyWithStrings(() => import("./details/CuratedWalkDetail.tsx").then(m => ({ default: m.CuratedWalkDetail })));

export interface DetailPanelProps {
  // The detail panel renders different layer-specific shapes via per-branch
  // narrowing. A precise union here would force every consumer to narrow up
  // front, which is exactly what the switch already does internally.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selected: any;
  selectedType: LayerType | null;
  enrichment: EnrichmentData | null;
  enrichLoading: boolean;
  lang: Lang;
  onClose: () => void;
  nearbyPins: NearbyPinItem[];
  onSelectNearby: (item: NearbyPinItem) => void;
  onNavigateRelation: (rel: WikidataRelation) => void;
  /**
   * The Danish folklore panel's own channel back to the map: the other places
   * its legend names, which live in the lazily fetched text row and so cannot
   * be read anywhere else. Pulled out below rather than left in `ua`, for the
   * same reason activeStopId is, so it does not ride into twenty other panels.
   */
  onFolkloreContext: (ctx: FolkloreContext | null) => void;
  /** Every legend one teller told, opened as the folklore panel's list. */
  onShowTeller?: (informant: string) => void;
  // User action props
  userData: UserData;
  markVisited: (id: string, v: UserSiteVisit) => void;
  unmarkVisited: (id: string) => void;
  toggleSaved: (id: string) => void;
  isVisited: (id: string) => boolean;
  isSaved: (id: string) => boolean;
  addBadge: (id: string) => void;
  addNote: (id: string, note: string) => void;
  onBadgeEarned: (badge: BadgeDef) => void;
  collections?: UserCollection[];
  onAddToCollection?: (collectionId: string, item: CollectionItem) => void;
  onRemoveFromCollection?: (collectionId: string, siteId: string) => void;
  onCreateCollection?: (name: string, emoji?: string) => void;
  isLoggedIn: boolean;
  onLoginRequest: (mode?: "login" | "signup") => void;
  onCompassActivate?: () => void;
  onBack?: () => void;
  /**
   * Walk-only. The panel is a numbered list rather than a single record, so
   * which stop is being read lives in App.tsx's `#walk=&stop=` and comes back
   * down here. Every other layer ignores both.
   */
  activeStopId?: string | null;
  onSelectWalkStop?: (stop: WalkStop) => void;
  /** Walk-only. Collapses the sheet to App.tsx's WalkModeBar. */
  onShowWalkMap?: () => void;
  onShowWalkPast?: (stop: import("../types/index.ts").WalkStop) => void;
}

function DetailPanelInner(props: DetailPanelProps) {
  // activeStopId and onSelectWalkStop are pulled out here rather than left in
  // `ua`, so they do not ride along in `common` into all twenty other panels.
  const { selected, selectedType, enrichment, enrichLoading, lang, onClose, nearbyPins, onSelectNearby, onNavigateRelation, onFolkloreContext, onShowTeller, activeStopId, onSelectWalkStop, onShowWalkMap, onShowWalkPast, ...ua } = props;
  if (!selected || !selectedType) return null;

  const userActions = {
    ...ua,
    onBadgeEarned: ua.onBadgeEarned,
    onCompassActivate: ua.onCompassActivate,
    isLoggedIn: ua.isLoggedIn,
    onLoginRequest: ua.onLoginRequest,
  };

  const common = { enrichment, enrichLoading, lang, onClose, onBack: props.onBack, nearbyPins, onSelectNearby, ...userActions };

  switch (selectedType) {
    case "heritage":
      return (
        <DetailErrorBoundary key={selected.id} siteId={selected.id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><HeritageDetail selected={selected} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "lighthouse":
      return (
        <DetailErrorBoundary key={selected.id} siteId={selected.id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><LighthouseDetail site={selected} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "folklore":
      return (
        <DetailErrorBoundary key={(selected as FolkloreRecord[])[0]?.id} siteId={(selected as FolkloreRecord[])[0]?.id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><FolkloreDetail records={selected as FolkloreRecord[]} {...common} onNavigateRelation={onNavigateRelation} onFolkloreContext={onFolkloreContext} onShowTeller={onShowTeller} /></Suspense>
        </DetailErrorBoundary>
      );
    case "runestone":
      return (
        <DetailErrorBoundary key={(selected as RuneStone).id} siteId={(selected as RuneStone).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><RunestoneDetail stone={selected as RuneStone} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "battlefield":
      return (
        <DetailErrorBoundary key={(selected as HistoricalEvent).id} siteId={(selected as HistoricalEvent).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><HistoricalEventDetail event={selected as HistoricalEvent} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "monument":
      return (
        <DetailErrorBoundary key={(selected as Monument).id} siteId={(selected as Monument).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><MonumentDetail monument={selected as Monument} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "building":
      return (
        <DetailErrorBoundary key={(selected as ProtectedBuilding).id} siteId={(selected as ProtectedBuilding).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><BuildingDetail building={selected as ProtectedBuilding} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "shipwreck":
      return (
        <DetailErrorBoundary key={(selected as Shipwreck).id} siteId={(selected as Shipwreck).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><ShipwreckDetail wreck={selected as Shipwreck} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "museum":
      return (
        <DetailErrorBoundary key={(selected as Museum).id} siteId={(selected as Museum).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><MuseumDetail museum={selected as Museum} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "rockart":
      return (
        <DetailErrorBoundary key={(selected as RockArt).id} siteId={(selected as RockArt).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><RockArtDetail art={selected as RockArt} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "unesco":
      return (
        <DetailErrorBoundary key={(selected as UnescoSite).id} siteId={(selected as UnescoSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><UnescoDetail site={selected as UnescoSite} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "industrial":
      return (
        <DetailErrorBoundary key={(selected as IndustrialSite).id} siteId={(selected as IndustrialSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><IndustrialDetail site={selected as IndustrialSite} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "barrow":
      return (
        <DetailErrorBoundary key={(selected as BarrowSite).id} siteId={(selected as BarrowSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><BarrowDetail site={selected as BarrowSite} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "megalith":
      return (
        <DetailErrorBoundary key={(selected as MegalithSite).id} siteId={(selected as MegalithSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><MegalithDetail site={selected as MegalithSite} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "townscape":
      return (
        <DetailErrorBoundary key={(selected as TownscapeSite).id} siteId={(selected as TownscapeSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><TownscapeDetail site={selected as TownscapeSite} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "plaque":
      return (
        <DetailErrorBoundary key={(selected as PlaqueSite).id} siteId={(selected as PlaqueSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><PlaqueDetail site={selected as PlaqueSite} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "stolperstein":
      return (
        <DetailErrorBoundary key={(selected as StolpersteinSite).id} siteId={(selected as StolpersteinSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><StolpersteinDetail site={selected as StolpersteinSite} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "thingsite":
      return (
        <DetailErrorBoundary key={(selected as ThingSite).id} siteId={(selected as ThingSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><ThingSiteDetail site={selected as ThingSite} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "route":
      return (
        <DetailErrorBoundary key={(selected as HistoricRoute).id} siteId={(selected as HistoricRoute).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><RouteDetail route={selected as HistoricRoute} {...common} onNavigateRelation={onNavigateRelation} /></Suspense>
        </DetailErrorBoundary>
      );
    case "byleksikon":
      return (
        <DetailErrorBoundary key={(selected as ByleksikonSite).id} siteId={(selected as ByleksikonSite).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><ByleksikonDetail site={selected as ByleksikonSite} {...common} /></Suspense>
        </DetailErrorBoundary>
      );
    case "walk":
      // A walk without its stops is a record that came in by name and position;
      // the panel needs the stops, and App opens the whole walk a moment later.
      if (!(selected as CuratedWalk).stops) return null;
      return (
        <DetailErrorBoundary key={(selected as CuratedWalk).id} siteId={(selected as CuratedWalk).id} layer={selectedType} lang={lang} onClose={onClose}>
          <Suspense fallback={null}><CuratedWalkDetail walk={selected as CuratedWalk} {...common} activeStopId={activeStopId} onSelectStop={onSelectWalkStop} onShowMap={onShowWalkMap} onShowPast={onShowWalkPast} /></Suspense>
        </DetailErrorBoundary>
      );
    default:
      return null;
  }
}

/** The panel, with the enrichment's loading state handed to the sheet body. */
export function DetailPanel(props: DetailPanelProps) {
  return (
    <EnrichPendingContext.Provider value={props.enrichLoading}>
      <DetailPanelInner {...props} />
    </EnrichPendingContext.Provider>
  );
}
